import React, { useState, useRef, useCallback, useEffect } from 'react';
import authService from '../../utils/authService';
import config from '../../config';

const CitySearch = ({ value = '', onCitySelect, placeholder = "Where are you going?" }) => {
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || ''); // Ensure it's never undefined
  const cityInputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const fetchCitySuggestions = useCallback(async (query) => {
    if (!query.trim()) {
      setCitySuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/api/search/cities?query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeader()
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('🏙️ City suggestions API response:', data);
        setCitySuggestions(data || []);
        setShowSuggestions(data.length > 0);
      }
    } catch (error) {
      console.error('Error fetching city suggestions:', error);
      setCitySuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const inputVal = e.target.value || ''; // Ensure it's never undefined
    setInputValue(inputVal);
    
    // Don't call onCitySelect when typing - only when selecting from suggestions
    // This prevents overwriting the state/country data
    
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new debounce timer for API call
    debounceTimerRef.current = setTimeout(() => {
      fetchCitySuggestions(inputVal);
    }, 300);
  };

  const selectCity = (city) => {
    const cityName = city.cityName || '';
    setInputValue(cityName);
    
    console.log('🏙️ City selected in CitySearch:', {
      cityName: city.cityName,
      stateName: city.stateName,
      countryName: city.countryName,
      fullObject: city
    });
    
    if (onCitySelect) {
      // Ensure we pass the complete city object with all required fields
      onCitySelect({
        cityName: city.cityName || '',
        stateName: city.stateName || '',
        countryName: city.countryName || ''
      });
    }
    
    setShowSuggestions(false);
    setCitySuggestions([]);
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle window resize and scroll to reposition dropdown
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (showSuggestions && cityInputRef.current) {
        const dropdown = document.querySelector('.city-suggestions');
        if (dropdown) {
          const rect = cityInputRef.current.getBoundingClientRect();
          const newTop = rect.bottom + 8;
          const newLeft = rect.left;
          const newWidth = Math.max(rect.width, 300);
          
          dropdown.style.top = `${newTop}px`;
          dropdown.style.left = `${newLeft}px`;
          dropdown.style.width = `${newWidth}px`;
        }
      }
    };

    if (showSuggestions) {
      // Update position immediately
      updateDropdownPosition();
      
      // Add event listeners for continuous updates
      window.addEventListener('scroll', updateDropdownPosition, { passive: true });
      window.addEventListener('resize', updateDropdownPosition, { passive: true });
      document.addEventListener('scroll', updateDropdownPosition, { passive: true, capture: true });
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
        document.removeEventListener('scroll', updateDropdownPosition, { capture: true });
      };
    }
  }, [showSuggestions]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityInputRef.current && !cityInputRef.current.contains(event.target)) {
        // Check if click is not on the suggestions dropdown
        const suggestionDropdown = document.querySelector('.city-suggestions');
        if (!suggestionDropdown || !suggestionDropdown.contains(event.target)) {
          setShowSuggestions(false);
        }
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  return (
    <div className="relative" style={{ zIndex: 1000 }}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <input
          ref={cityInputRef}
          type="text"
          value={inputValue} // Always controlled - never undefined
          onChange={handleInputChange}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white hover:border-gray-400 shadow-sm"
          required
          autoComplete="off"
        />
        {inputValue && (
          <button
            type="button"
            onClick={() => {
              setInputValue('');
              if (onCitySelect) {
                onCitySelect({ cityName: '', stateName: '', countryName: '' });
              }
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
      {showSuggestions && citySuggestions.length > 0 && (
        <div className="city-suggestions fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" 
             style={{ 
               zIndex: 9999,
               top: cityInputRef.current ? cityInputRef.current.getBoundingClientRect().bottom + window.scrollY + 8 : 'auto',
               left: cityInputRef.current ? cityInputRef.current.getBoundingClientRect().left + window.scrollX : 'auto',
               width: cityInputRef.current ? cityInputRef.current.getBoundingClientRect().width : 'auto',
               minWidth: '300px'
             }}>
          {citySuggestions.map((city, index) => (
            <div
              key={index}
              onMouseDown={(e) => {
                e.preventDefault();
                selectCity(city);
              }}
              onClick={(e) => {
                e.stopPropagation();
                selectCity(city);
              }}
              className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-200"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{city.cityName}</div>
                  <div className="text-sm text-gray-600 truncate">
                    {city.stateName && `${city.stateName}, `}{city.countryName}
                  </div>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CitySearch;